import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import SiteLayout from "@/components/layout/SiteLayout";
import Seo, { SITE_NAME, SITE_URL } from "@/components/base/Seo";
import SectionHeading from "@/components/base/SectionHeading";
import HowItWorks from "@/components/feature/HowItWorks";
import { getPlans } from "@/services/plansService";
import { isBoostPlan } from "@/utils/plans";
import type { Plan, PlansBundle } from "@/types/plans";
import PlanCard from "./components/PlanCard";
import ComparisonTable from "./components/ComparisonTable";
import PlanFaq from "./components/PlanFaq";
import UpgradeModal from "./components/UpgradeModal";

export default function PlansPage() {
  const [bundle, setBundle]   = useState<PlansBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [upgradeTarget, setUpgradeTarget] = useState<Plan | null>(null);
  const location = useLocation();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setBundle(await getPlans());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the listing plans. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-scroll to the plan anchor when navigated from portal profile
  useEffect(() => {
    if (location.hash && bundle) {
      const el = document.querySelector(location.hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "center" }), 150);
      }
    }
  }, [location.hash, bundle]);

  const plans = bundle?.plans ?? [];
  const corePlans = plans.filter((plan) => !isBoostPlan(plan.slug));
  const boostPlans = plans.filter((plan) => isBoostPlan(plan.slug));

  return (
    <SiteLayout>
      <Seo
        title="Business Listing Plans & Pricing"
        description="Compare free, business and premium listing plans on Coastal Bizz Connect. Reach customers across South Canara, Udupi and North Canara with verified profiles, lead tools and priority ranking."
        keywords="business listing plans Karnataka, advertise business Mangaluru, Udupi business directory pricing, North Canara business listing"
        path="/listing-plans"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Business Listing Plans & Pricing",
          url: `${SITE_URL}/listing-plans`,
          isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
        }}
      />

      <section className="border-b border-background-200 bg-primary-900">
        <div className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-20">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-background-300" aria-label="Breadcrumb">
            <Link to="/" className="transition-colors hover:text-accent-300">
              Home
            </Link>
            <span className="flex h-3.5 w-3.5 items-center justify-center">
              <i className="ri-arrow-right-s-line text-sm" />
            </span>
            <span className="font-medium text-background-100">Listing plans</span>
          </nav>

          <div className="mt-5 max-w-3xl">
            <h1 className="font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-4xl">
              Plans that grow with your business
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-background-300 md:text-base">
              Start with a free listing and upgrade whenever you are ready for more visibility, leads
              and customer reach across the Canara coast. No hidden charges, cancel anytime.
            </p>
          </div>

          <p className="mt-7 inline-flex items-center gap-2 rounded-full border border-primary-700 bg-primary-950/40 px-5 py-2 text-sm font-semibold text-background-100">
            <span className="flex h-4 w-4 items-center justify-center text-accent-400">
              <i className="ri-calendar-check-line text-base" />
            </span>
            Simple yearly pricing — one payment covers 12 months
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-16">
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-400" />
            <p className="text-sm text-foreground-500">Loading plans…</p>
          </div>
        ) : error ? (
          <div className="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-lg border border-rose-200 bg-rose-50 px-6 py-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <i className="ri-error-warning-line text-2xl" />
            </span>
            <div>
              <p className="font-heading text-base font-bold text-foreground-950">Couldn&apos;t load plans</p>
              <p className="mt-1 text-sm text-foreground-600">{error}</p>
            </div>
            <button
              type="button"
              onClick={load}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-refresh-line text-base" /> Try again
            </button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 pt-3 md:grid-cols-3">
              {corePlans.map((plan) => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onUpgrade={() => setUpgradeTarget(plan)}
                />
              ))}
            </div>

            {boostPlans.length > 0 ? (
              <div className="mt-14">
                <div className="flex flex-col items-center text-center">
                  <span className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-accent-100 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-accent-900">
                    <span className="flex h-4 w-4 items-center justify-center">
                      <i className="ri-rocket-2-line text-sm" />
                    </span>
                    Boost your visibility
                  </span>
                  <h2 className="mt-4 font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
                    Short-term placement plans
                  </h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                    Renewable 30-day boosts that put your listing in front of more customers — on the
                    homepage, and across your district, taluk and chosen category pages.
                  </p>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                  {boostPlans.map((plan) => (
                    <PlanCard
                      key={plan.id}
                      plan={plan}
                      onUpgrade={() => setUpgradeTarget(plan)}
                    />
                  ))}
                </div>
              </div>
            ) : null}
          </>
        )}

        <div className="mt-10 flex flex-col items-start justify-between gap-5 rounded-lg border border-secondary-200 bg-secondary-50 p-6 md:flex-row md:items-center md:p-8">
          <div className="flex items-start gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
              <i className="ri-building-4-line text-xl" />
            </span>
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">
                Chains, franchises & multi-location brands
              </h2>
              <p className="mt-1 max-w-2xl text-sm leading-relaxed text-foreground-600">
                Manage several branches under one dashboard with a custom plan built around your
                footprint across the three coastal districts.
              </p>
            </div>
          </div>
          <Link
            to="/contact"
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            Talk to our team
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-right-line text-base" />
            </span>
          </Link>
        </div>
      </section>

      {!loading && !error && bundle && bundle.comparison.length > 0 && (
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-16">
            <SectionHeading
              eyebrow="Compare plans"
              title="Everything included, side by side"
              description="A transparent breakdown of what each plan gives your business — from featured images and gallery limits to lead tools and SEO."
            />
            <div className="mt-8">
              <ComparisonTable rows={bundle.comparison} />
            </div>
          </div>
        </section>
      )}

      <HowItWorks />

      {!loading && !error && bundle && bundle.faqs.length > 0 && (
        <section className="border-y border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1400px] px-6 py-14 md:py-16">
            <SectionHeading
              eyebrow="Plans FAQ"
              title="Questions about plans and billing"
              description="Everything you need to know before choosing a plan — billing, upgrades, expiry and custom options."
            />
            <div className="mt-8">
              <PlanFaq faqs={bundle.faqs} />
            </div>
          </div>
        </section>
      )}

      <section className="bg-primary-900">
        <div className="mx-auto flex w-full max-w-[1400px] flex-col items-center gap-6 px-6 py-14 text-center md:py-16">
          <h2 className="font-heading text-2xl font-extrabold text-background-50 md:text-3xl">
            Ready to get found on the coast?
          </h2>
          <p className="max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            List your business in minutes. Start free, upgrade when your leads start flowing.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/list-your-business"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors duration-200 hover:bg-accent-600"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-add-circle-line text-base" />
              </span>
              List your business
            </Link>
            <Link
              to="/directory"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-700 px-6 py-3 text-sm font-semibold text-background-100 transition-colors duration-200 hover:border-accent-400 hover:text-accent-300"
            >
              Browse the directory
            </Link>
          </div>
        </div>
      </section>

      {/* Upgrade modal — opens when a plan CTA is clicked */}
      {upgradeTarget && (
        <UpgradeModal
          plan={upgradeTarget}
          onClose={() => setUpgradeTarget(null)}
        />
      )}
    </SiteLayout>
  );
}